import { useState } from "react";
import { Save, Users, Bell, Building2, Plus, Pencil, X, Tag } from "lucide-react";
import { clsx } from "clsx";
import { useAuth } from "@/auth/useAuth";
import { useTenants, useCreateTenant, useUpdateTenant } from "@/hooks/useTenants";
import { useTenantPricingRule } from "@/hooks/useBilling";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { TenantPricingEditor } from "@/components/billing/TenantPricingEditor";
import type { Tenant, TenantCreatePayload } from "@/types";

type SettingsTab = "tenant" | "users" | "notifications";

interface TenantFormData {
  name: string;
  slug: string;
  client_number: string;
  quota_vcpu: number;
  quota_ram_gb: number;
  quota_disk_gb: number;
}

const EMPTY_FORM: TenantFormData = {
  name: "",
  slug: "",
  client_number: "",
  quota_vcpu: 8,
  quota_ram_gb: 16,
  quota_disk_gb: 100,
};

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Settings page providing three tabbed sections:
 *
 * - **Tenants** -- List, create, and edit tenant organizations (admin only
 *   for create/edit).  Each tenant has a name, slug, optional client number,
 *   and resource quotas (vCPU, RAM, disk).
 * - **Users** -- Links to the Keycloak admin console for managing user
 *   accounts, roles (admin, tenant_admin, operator, viewer), and group
 *   memberships.  Only accessible to admin and tenant_admin roles.
 * - **Notifications** -- Configure alert channels (email, ntfy) and
 *   resource usage thresholds for CPU, RAM, and disk alerts.  Preferences
 *   are currently client-side only (not yet persisted to the backend).
 */
export default function Settings() {
  const [activeTab, setActiveTab] = useState<SettingsTab>("tenant");
  const { isAdmin, isTenantAdmin } = useAuth();

  // Tenant management state
  const { data: tenants, isLoading: tenantsLoading } = useTenants();
  const createMutation = useCreateTenant();
  const updateMutation = useUpdateTenant();
  const [showForm, setShowForm] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [form, setForm] = useState<TenantFormData>(EMPTY_FORM);

  // Tenant pricing editor state
  const [pricingTenant, setPricingTenant] = useState<Tenant | null>(null);

  // Notification preferences
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [ntfyNotifications, setNtfyNotifications] = useState(true);
  const [alertThresholdCpu, setAlertThresholdCpu] = useState(90);
  const [alertThresholdRam, setAlertThresholdRam] = useState(85);
  const [alertThresholdDisk, setAlertThresholdDisk] = useState(90);

  const tabs = [
    { id: "tenant" as const, label: "Tenants", icon: Building2 },
    {
      id: "users" as const,
      label: "Users",
      icon: Users,
      disabled: !isAdmin && !isTenantAdmin,
    },
    { id: "notifications" as const, label: "Notifications", icon: Bell },
  ];

  const openCreateForm = () => {
    setEditingTenant(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEditForm = (tenant: Tenant) => {
    setEditingTenant(tenant);
    setForm({
      name: tenant.name,
      slug: tenant.slug,
      client_number: tenant.client_number ?? "",
      quota_vcpu: tenant.quota_vcpu,
      quota_ram_gb: tenant.quota_ram_gb,
      quota_disk_gb: tenant.quota_disk_gb,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingTenant(null);
  };

  const handleNameChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      name: value,
      slug: editingTenant ? prev.slug : slugify(value),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingTenant) {
      updateMutation.mutate(
        {
          id: String(editingTenant.id),
          payload: {
            name: form.name,
            client_number: form.client_number || undefined,
            quota_vcpu: form.quota_vcpu,
            quota_ram_gb: form.quota_ram_gb,
            quota_disk_gb: form.quota_disk_gb,
          },
        },
        { onSuccess: closeForm }
      );
    } else {
      const payload: TenantCreatePayload = {
        name: form.name,
        slug: form.slug,
        client_number: form.client_number || undefined,
        quota_vcpu: form.quota_vcpu,
        quota_ram_gb: form.quota_ram_gb,
        quota_disk_gb: form.quota_disk_gb,
      };
      createMutation.mutate(payload, { onSuccess: closeForm });
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const saveError = createMutation.error || updateMutation.error;

  function PricingBadge({ tenantId }: { tenantId: number }) {
    const { data: rule } = useTenantPricingRule(tenantId);
    return (
      <span
        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
          rule
            ? "bg-blue-500/20 text-blue-400"
            : "bg-gray-700/50 text-gray-500"
        }`}
      >
        {rule ? "Custom" : "Global"}
      </span>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-100">Settings</h1>
        <p className="mt-1 text-sm text-gray-400">
          Manage tenants, users, and notification preferences
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 rounded-lg border border-gray-800 bg-gray-900/50 p-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => !tab.disabled && setActiveTab(tab.id)}
            disabled={tab.disabled}
            className={clsx(
              "flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors",
              activeTab === tab.id
                ? "bg-gray-800 text-gray-100"
                : tab.disabled
                  ? "cursor-not-allowed text-gray-600"
                  : "text-gray-400 hover:text-gray-200"
            )}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tenant Management */}
      {activeTab === "tenant" && (
        <div className="space-y-4">
          {/* Tenant Form (create/edit) */}
          {showForm && (
            <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-6">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-200">
                  {editingTenant ? "Edit Tenant" : "New Tenant"}
                </h2>
                <button
                  onClick={closeForm}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-800 hover:text-gray-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm text-gray-400">
                      Name
                    </label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => handleNameChange(e.target.value)}
                      placeholder="Acme Corp"
                      className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm text-gray-400">
                      Slug
                    </label>
                    <input
                      type="text"
                      required
                      value={form.slug}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, slug: e.target.value }))
                      }
                      disabled={!!editingTenant}
                      placeholder="acme-corp"
                      className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:border-blue-500 focus:outline-none disabled:opacity-50"
                    />
                    <p className="mt-0.5 text-xs text-gray-500">
                      Lowercase, hyphens only
                    </p>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm text-gray-400">
                      Client Number
                    </label>
                    <input
                      type="text"
                      value={form.client_number}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          client_number: e.target.value,
                        }))
                      }
                      placeholder="CLI-00042"
                      className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:border-blue-500 focus:outline-none"
                    />
                    <p className="mt-0.5 text-xs text-gray-500">
                      Internal reference number
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="mb-2 text-sm font-medium text-gray-300">
                    Quotas
                  </h3>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div>
                      <label className="mb-1 block text-sm text-gray-400">
                        vCPU
                      </label>
                      <input
                        type="number"
                        min={0}
                        value={form.quota_vcpu}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            quota_vcpu: parseInt(e.target.value) || 0,
                          }))
                        }
                        className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm text-gray-400">
                        RAM (GB)
                      </label>
                      <input
                        type="number"
                        min={0}
                        value={form.quota_ram_gb}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            quota_ram_gb: parseInt(e.target.value) || 0,
                          }))
                        }
                        className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm text-gray-400">
                        Disk (GB)
                      </label>
                      <input
                        type="number"
                        min={0}
                        value={form.quota_disk_gb}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            quota_disk_gb: parseInt(e.target.value) || 0,
                          }))
                        }
                        className="w-full rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Save className="h-4 w-4" />
                    {isSaving
                      ? "Saving..."
                      : editingTenant
                        ? "Update Tenant"
                        : "Create Tenant"}
                  </button>
                  <button
                    type="button"
                    onClick={closeForm}
                    className="rounded-lg border border-gray-700 px-4 py-2 text-sm text-gray-300 hover:bg-gray-800"
                  >
                    Cancel
                  </button>
                </div>

                {saveError && (
                  <p className="text-sm text-red-400">
                    Failed to save: {(saveError as Error).message}
                  </p>
                )}
              </form>
            </div>
          )}

          {/* Tenant List */}
          <div className="rounded-xl border border-gray-800 bg-gray-900/50">
            <div className="flex items-center justify-between border-b border-gray-800 px-6 py-4">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-blue-400" />
                <h2 className="text-base font-semibold text-gray-200">
                  Tenants
                </h2>
              </div>
              {isAdmin && !showForm && (
                <button
                  onClick={openCreateForm}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-700 px-3 py-1.5 text-sm text-gray-300 transition-colors hover:bg-gray-800"
                >
                  <Plus className="h-4 w-4" />
                  New Tenant
                </button>
              )}
            </div>

            {tenantsLoading ? (
              <div className="flex justify-center p-8">
                <LoadingSpinner />
              </div>
            ) : tenants && tenants.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-800 text-left text-gray-400">
                      <th className="px-6 py-3 font-medium">Name</th>
                      <th className="px-6 py-3 font-medium">Slug</th>
                      <th className="px-6 py-3 font-medium text-center">Role</th>
                      <th className="px-6 py-3 font-medium text-center">Omer-CLI</th>
                      <th className="px-6 py-3 font-medium">Client #</th>
                      <th className="px-6 py-3 font-medium text-center">
                        vCPU
                      </th>
                      <th className="px-6 py-3 font-medium text-center">
                        RAM (GB)
                      </th>
                      <th className="px-6 py-3 font-medium text-center">
                        Disk (GB)
                      </th>
                      <th className="px-6 py-3 font-medium text-center">
                        Pricing
                      </th>
                      <th className="px-6 py-3 font-medium">Created</th>
                      {isAdmin && (
                        <th className="px-6 py-3 font-medium text-right">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {tenants.map((tenant) => (
                      <tr
                        key={tenant.id}
                        className="border-b border-gray-800/50 hover:bg-gray-800/30"
                      >
                        <td className="px-6 py-3 font-medium text-gray-200">
                          {tenant.name}
                        </td>
                        <td className="px-6 py-3 font-mono text-xs text-gray-400">
                          {tenant.slug}
                        </td>
                        <td className="px-6 py-3 text-center">
                          <span
                            className={clsx(
                              "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
                              tenant.role === "editor"
                                ? "bg-amber-500/20 text-amber-400"
                                : tenant.role === "reseller"
                                  ? "bg-purple-500/20 text-purple-400"
                                  : "bg-gray-700/50 text-gray-400"
                            )}
                          >
                            {tenant.role}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-center">
                          <span
                            className={clsx(
                              "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
                              tenant.omer_cli_enabled
                                ? "bg-violet-500/20 text-violet-400"
                                : "bg-gray-700/50 text-gray-500"
                            )}
                          >
                            {tenant.omer_cli_enabled ? "Active" : "Off"}
                          </span>
                        </td>
                        <td className="px-6 py-3 font-mono text-xs text-gray-300">
                          {tenant.client_number ?? "-"}
                        </td>
                        <td className="px-6 py-3 text-center text-gray-300">
                          {tenant.quota_vcpu}
                        </td>
                        <td className="px-6 py-3 text-center text-gray-300">
                          {tenant.quota_ram_gb}
                        </td>
                        <td className="px-6 py-3 text-center text-gray-300">
                          {tenant.quota_disk_gb}
                        </td>
                        <td className="px-6 py-3 text-center">
                          {isAdmin ? (
                            <button
                              onClick={() => setPricingTenant(tenant)}
                              title="Manage pricing"
                              className="transition-opacity hover:opacity-80"
                            >
                              <PricingBadge tenantId={tenant.id} />
                            </button>
                          ) : (
                            <PricingBadge tenantId={tenant.id} />
                          )}
                        </td>
                        <td className="px-6 py-3 text-gray-400">
                          {new Date(tenant.created_at).toLocaleDateString(
                            "fr-FR"
                          )}
                        </td>
                        {isAdmin && (
                          <td className="px-6 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => setPricingTenant(tenant)}
                                title="Manage pricing"
                                className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-800 hover:text-blue-400"
                              >
                                <Tag className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => openEditForm(tenant)}
                                title="Edit"
                                className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-800 hover:text-blue-400"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">
                No tenants configured yet.
              </div>
            )}
          </div>

          {/* Tenant Pricing Editor */}
          {pricingTenant && (
            <TenantPricingEditor
              tenantId={pricingTenant.id}
              tenantName={pricingTenant.name}
              onClose={() => setPricingTenant(null)}
            />
          )}
        </div>
      )}

      {/* User Management */}
      {activeTab === "users" && (
        <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-6">
          <h2 className="mb-6 text-lg font-semibold text-gray-200">
            User Management
          </h2>
          <p className="text-sm text-gray-400">
            Users are managed through Keycloak. Use the Keycloak admin console
            to manage user accounts, roles, and group memberships for this
            tenant.
          </p>
          <div className="mt-4">
            <a
              href={`${import.meta.env.VITE_KEYCLOAK_URL || "http://localhost:8080"}/admin/${import.meta.env.VITE_KEYCLOAK_REALM || "alphaprox"}/console`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg bg-gray-700 px-4 py-2.5 text-sm font-medium text-gray-200 transition-colors hover:bg-gray-600"
            >
              <Users className="h-4 w-4" />
              Open Keycloak Admin
            </a>
          </div>

          {/* Roles Reference */}
          <div className="mt-8">
            <h3 className="mb-3 text-base font-medium text-gray-300">
              Available Roles
            </h3>
            <div className="space-y-2">
              {[
                {
                  role: "admin",
                  desc: "Full access to all tenants and system settings",
                },
                {
                  role: "tenant_admin",
                  desc: "Manage VMs, backups, and users within the tenant",
                },
                {
                  role: "operator",
                  desc: "Start, stop, and monitor VMs; view backups",
                },
                {
                  role: "viewer",
                  desc: "Read-only access to VM and monitoring data",
                },
              ].map((r) => (
                <div
                  key={r.role}
                  className="flex items-center gap-3 rounded-lg border border-gray-800 p-3"
                >
                  <span className="rounded bg-blue-500/20 px-2 py-0.5 text-xs font-mono font-medium text-blue-400">
                    {r.role}
                  </span>
                  <span className="text-sm text-gray-400">{r.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Notification Preferences */}
      {activeTab === "notifications" && (
        <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-6">
          <h2 className="mb-6 text-lg font-semibold text-gray-200">
            Notification Preferences
          </h2>
          <form
            className="max-w-lg space-y-6"
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            {/* Channels */}
            <div>
              <h3 className="mb-3 text-sm font-medium text-gray-300">
                Notification Channels
              </h3>
              <div className="space-y-3">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={emailNotifications}
                    onChange={(e) => setEmailNotifications(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-300">
                    Email notifications
                  </span>
                </label>
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={ntfyNotifications}
                    onChange={(e) => setNtfyNotifications(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-300">
                    ntfy push notifications
                  </span>
                </label>
              </div>
            </div>

            {/* Alert Thresholds */}
            <div>
              <h3 className="mb-3 text-sm font-medium text-gray-300">
                Alert Thresholds
              </h3>
              <div className="space-y-4">
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <label className="text-sm text-gray-400">
                      CPU Usage Alert
                    </label>
                    <span className="text-sm font-mono text-gray-300">
                      {alertThresholdCpu}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min={50}
                    max={100}
                    value={alertThresholdCpu}
                    onChange={(e) =>
                      setAlertThresholdCpu(Number(e.target.value))
                    }
                    className="w-full accent-blue-500"
                  />
                </div>
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <label className="text-sm text-gray-400">
                      RAM Usage Alert
                    </label>
                    <span className="text-sm font-mono text-gray-300">
                      {alertThresholdRam}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min={50}
                    max={100}
                    value={alertThresholdRam}
                    onChange={(e) =>
                      setAlertThresholdRam(Number(e.target.value))
                    }
                    className="w-full accent-purple-500"
                  />
                </div>
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <label className="text-sm text-gray-400">
                      Disk Usage Alert
                    </label>
                    <span className="text-sm font-mono text-gray-300">
                      {alertThresholdDisk}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min={50}
                    max={100}
                    value={alertThresholdDisk}
                    onChange={(e) =>
                      setAlertThresholdDisk(Number(e.target.value))
                    }
                    className="w-full accent-green-500"
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700"
            >
              <Save className="h-4 w-4" />
              Save Preferences
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
